import { createTheme } from "@mui/material/styles";
import { gradients } from "./styles/gradients";

const theme = createTheme({
  palette: {
    primary: {
      main: gradients.colors.mediumBlue,
      light: gradients.colors.slateLight,
      dark: gradients.colors.darkBlue,
      contrastText: "#ffffff",
    },
    secondary: {
      main: gradients.colors.brightBlue,
      light: gradients.colors.lightBlue,
      dark: gradients.colors.slate,
      contrastText: "#ffffff",
    },
    info: {
      main: gradients.colors.slateLight,
      contrastText: "#ffffff",
    },
    background: {
      default: gradients.colors.cloud,
      paper: "#FFFFFF",
    },
    text: {
      primary: "#0F172A",
      secondary: "#475569",
    },
    divider: gradients.colors.line,
    grey: {
      50: "#FFFFFF",
      100: "#F7F9FC",
      200: gradients.colors.line,
      300: "#CBD5E1",
      400: "#94A3B8",
      500: "#64748B",
      600: "#475569",
      700: "#334155",
      800: "#1E293B",
      900: "#0F172A",
    },
  },
  gradients,
  brandScale: gradients.scale,
  typography: {
    fontFamily:
      '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", "Helvetica Neue", Arial, sans-serif',
    h1: {
      fontFamily: '"Space Grotesk", "Inter", "Segoe UI", sans-serif',
      fontWeight: 700,
      letterSpacing: "-0.025em",
      fontSize: "2.875rem",
      lineHeight: 1.08,
      color: "#0F172A",
    },
    h2: {
      fontFamily: '"Space Grotesk", "Inter", "Segoe UI", sans-serif',
      fontWeight: 700,
      letterSpacing: "-0.02em",
      fontSize: "2.375rem",
      lineHeight: 1.18,
      color: "#0F172A",
    },
    h3: {
      fontFamily: '"Space Grotesk", "Inter", "Segoe UI", sans-serif',
      fontWeight: 600,
      letterSpacing: "-0.015em",
      fontSize: "1.875rem",
      lineHeight: 1.28,
      color: "#0F172A",
    },
    h4: {
      fontWeight: 600,
      letterSpacing: 0,
      fontSize: "1.375rem",
      lineHeight: 1.35,
      color: "#0F172A",
    },
    h5: {
      fontWeight: 600,
      fontSize: "1.125rem",
      lineHeight: 1.45,
      color: "#0F172A",
    },
    h6: {
      fontWeight: 600,
      fontSize: "1rem",
      lineHeight: 1.5,
      color: "#0F172A",
    },
    body1: {
      fontSize: "1rem",
      lineHeight: 1.7,
      color: "#475569",
      fontWeight: 400,
    },
    body2: {
      fontSize: "0.875rem",
      lineHeight: 1.6,
      color: "#475569",
    },
    button: {
      fontWeight: 600,
      letterSpacing: 0,
      textTransform: "none",
    },
  },
  shape: {
    borderRadius: gradients.scale.radius.lg,
  },
  shadows: [
    "none",
    "0 1px 3px 0 rgba(0, 0, 0, 0.05), 0 1px 2px 0 rgba(0, 0, 0, 0.1)",
    "0 4px 6px -1px rgba(0, 0, 0, 0.05), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
    "0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)",
    "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
    "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
    "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
    "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
    "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
    "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
    "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
    "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
    "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
    "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
    "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
    "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
    "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
    "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
    "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
    "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
    "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
    "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
    "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
    "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
    "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
  ],
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: "none",
          fontWeight: 600,
          borderRadius: gradients.scale.radius.lg,
          padding: "11px 26px",
          fontSize: "0.9375rem",
          letterSpacing: 0,
          boxShadow: "none",
          transition: "all 0.22s cubic-bezier(0.4, 0, 0.2, 1)",
          "&:hover": {
            boxShadow: "0 8px 18px -6px rgba(15, 23, 42, 0.18), 0 4px 8px -2px rgba(15, 23, 42, 0.08)",
            transform: "translateY(-1px)",
          },
        },
        contained: {
          backgroundColor: "#0F172A",
          border: "none",
          color: "white",
          "&:hover": {
            backgroundColor: "#1E293B",
            boxShadow: "0 14px 28px -10px rgba(15, 23, 42, 0.28), 0 6px 12px -4px rgba(15, 23, 42, 0.12)",
            transform: "translateY(-1px)",
          },
        },
        outlined: {
          borderWidth: "1.5px",
          borderColor: gradients.colors.mediumBlue,
          "&:hover": {
            borderWidth: "1.5px",
            borderColor: gradients.colors.darkBlue,
            backgroundColor: "rgba(248, 250, 252, 0.95)",
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          boxShadow: "0 1px 2px rgba(15, 23, 42, 0.04), 0 1px 1px rgba(15, 23, 42, 0.03)",
          border: "1px solid",
          borderColor: "divider",
          borderRadius: gradients.scale.radius.xl,
          overflow: "hidden",
          transition: "all 0.28s cubic-bezier(0.4, 0, 0.2, 1)",
          backgroundColor: "background.paper",
          "&:hover": {
            boxShadow: "0 4px 12px -4px rgba(15, 23, 42, 0.10), 0 2px 4px -2px rgba(15, 23, 42, 0.06)",
          },
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          "& .MuiOutlinedInput-root": {
            borderRadius: 8,
            "&:hover .MuiOutlinedInput-notchedOutline": {
              borderColor: gradients.colors.mediumBlue,
            },
            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
              borderColor: gradients.colors.darkBlue,
              borderWidth: "2px",
            },
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 6,
          fontWeight: 500,
          fontSize: "0.8125rem",
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          boxShadow: "0 1px 3px 0 rgba(0, 0, 0, 0.05), 0 1px 2px 0 rgba(0, 0, 0, 0.1)",
        },
      },
    },
  },
});

export default theme;
